import { useEffect, useState, useCallback } from "react";
import hero1 from "@/assets/hero-1.jpg";
import hero2 from "@/assets/hero-2.jpg";
import hero3 from "@/assets/hero-3.jpg";

const images = [hero1, hero2, hero3];
const HOLD = 4000;
const SLIDE_MS = 1200;

/**
 * All 3 images are always mounted (no src swap, no unmount).
 * Active image sits at translateX(0).
 * Others sit at translateX(-100%) off-screen left.
 * On slide: active → translateX(100%)  (exits right)
 *           next  → translateX(0)      (enters from left)
 * onTransitionEnd commits the new active index — no flicker.
 */
const HeroBackground = () => {
  const [active, setActive] = useState(0);
  const [nextIdx, setNextIdx] = useState<number | null>(null);

  // Start a new slide after HOLD ms
  useEffect(() => {
    if (nextIdx !== null) return; // already sliding
    const timer = window.setTimeout(() => {
      setNextIdx((active + 1) % images.length);
    }, HOLD);
    return () => clearTimeout(timer);
  }, [active, nextIdx]);

  // Commit after CSS transition ends
  const handleTransitionEnd = useCallback(() => {
    if (nextIdx === null) return;
    setActive(nextIdx);
    setNextIdx(null);
  }, [nextIdx]);

  const getStyle = (i: number): React.CSSProperties => {
    const isActive = i === active;
    const isNext = i === nextIdx;
    const isSliding = nextIdx !== null;

    let tx = "-100%"; // default: parked off-screen left
    let transition = "none";
    let zIndex = 0;

    if (isActive) {
      zIndex = 1;
      if (isSliding) {
        // slide out to the right
        tx = "100%";
        transition = `transform ${SLIDE_MS}ms ease-in-out`;
      } else {
        tx = "0%";
      }
    } else if (isNext) {
      zIndex = 2;
      // slide in from left to center
      tx = "0%";
      transition = `transform ${SLIDE_MS}ms ease-in-out`;
    }

    return {
      transform: `translateX(${tx})`,
      transition,
      zIndex,
      filter: "blur(0.5px)",
    };
  };

  return (
    <div className="absolute inset-0 overflow-hidden">
      {images.map((src, i) => (
        <img
          key={i}
          src={src}
          alt=""
          width={1920}
          height={1080}
          className="absolute inset-0 w-full h-full object-cover"
          style={getStyle(i)}
          onTransitionEnd={i === nextIdx ? handleTransitionEnd : undefined}
        />
      ))}

      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          zIndex: 3,
          background:
            "radial-gradient(ellipse 70% 65% at 50% 50%, transparent 40%, hsl(220, 20%, 95%) 100%)",
        }}
      />
    </div>
  );
};

export default HeroBackground;
